import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createCoaching, updateCoaching, fetchCoaching, clearCoaching } from '../store/slices/coachingsSlice';
import { fetchClients } from '../store/slices/clientsSlice';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  ArrowLeft, Save, DollarSign, Calendar,
  FileText, CreditCard, Upload, Paperclip, X, Briefcase, Users,
} from 'lucide-react';

/* ── Constantes ── */
const DOMAINES = [
  { value: 'action',     label: 'ACTION' },
  { value: 'obligation', label: 'OBLIGATION' },
  { value: 'autre',      label: 'AUTRE' },
];
const TYPE_COACHING = [
  { value: 'individuel', label: 'Individuel' },
  { value: 'groupe',     label: 'Groupe' },
];
const PAIEMENTS = [
  { value: 'impaye',  label: 'Impayé' },
  { value: 'partiel', label: 'Partiel' },
  { value: 'paye',    label: 'Payé' },
];
const SGI_OPTIONS = [
  { value: '',                       label: '— Non précisé —' },
  { value: 'sgi_bs',                 label: 'SGI BS' },
  { value: 'sgi_pcm',                label: 'SGI PCM' },
  { value: 'nsia_finance',           label: 'NSIA FINANCE' },
  { value: 'sgi_bici_bourse',        label: 'SGI BICI BOURSE' },
  { value: 'societe_generale',       label: 'SOCIÉTÉ GÉNÉRALE' },
  { value: 'atlantique_finance',     label: 'ATLANTIQUE FINANCE' },
  { value: 'boa_capital_securities', label: 'BOA CAPITAL SECURITIES' },
  { value: 'autre',                  label: 'Autre' },
];
const TYPE_DOC_OPTIONS = [
  { value: 'contrat',   label: 'Contrat signé' },
  { value: 'cni',       label: 'CNI' },
  { value: 'passeport', label: 'Passeport' },
  { value: 'fiche_id',  label: "Fiche d'identification" },
  { value: 'avenant',   label: 'Avenant' },
  { value: 'autre',     label: 'Autre document' },
];

/* ── Styles compacts (même approche que ClientFormPage) ── */
const S = {
  card:   { margin: 0, padding: '10px 14px', display: 'flex', flexDirection: 'column', minHeight: 0 },
  head:   { display: 'flex', alignItems: 'center', gap: 7, paddingBottom: 8,
            borderBottom: '1px solid #E5E7EB', marginBottom: 10, flexShrink: 0 },
  htitle: { fontSize: 11, fontWeight: 700, color: '#1F4E79', margin: 0,
            textTransform: 'uppercase', letterSpacing: '0.05em' },
  label:  { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 },
  grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  fg:     { margin: 0 },
  err:    { color: '#C00000', fontSize: 10, marginTop: 2 },
  inp:    { fontSize: 12, padding: '6px 10px' },
};

function SHead({ icon: Icon, title, color = '#1F4E79' }) {
  return (
    <div style={S.head}>
      <Icon size={13} color={color} />
      <span style={{ ...S.htitle, color }}>{title}</span>
    </div>
  );
}

/* ══════════════════════════════════════════
   PAGE PRINCIPALE
══════════════════════════════════════════ */
export default function CoachingFormPage() {
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { coaching, loadingOne } = useSelector(s => s.coachings);
  const { liste: clients }       = useSelector(s => s.clients);

  const [dateFin,   setDateFin]   = useState('');
  const [documents, setDocuments] = useState({});
  const fileRefs = useRef({});

  const { register, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      client:          '',
      type_coaching:   'individuel',
      domaine:         '',
      sgi:             '',
      prix:            '',
      date_debut:      '',
      duree_mois:      '',
      montant_paye:    '0',
      statut_paiement: 'impaye',
      statut:          'en_cours',
      observation:     '',
    },
  });

  const dateDebut = watch('date_debut');
  const dureeMois = watch('duree_mois');

  useEffect(() => {
    dispatch(fetchClients({ statut_cli: 'actif' }));
    if (isEdit) dispatch(fetchCoaching(id));
    return () => { dispatch(clearCoaching()); };
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && coaching) {
      reset({
        client:          coaching.client || '',
        type_coaching:   coaching.type_coaching || 'individuel',
        domaine:         coaching.domaine || '',
        sgi:             coaching.sgi || '',
        prix:            coaching.prix || '',
        date_debut:      coaching.date_debut || '',
        duree_mois:      coaching.duree_mois || '',
        montant_paye:    coaching.montant_paye || '0',
        statut_paiement: coaching.statut_paiement || 'impaye',
        statut:          coaching.statut || 'en_cours',
        observation:     coaching.observation || '',
      });
    }
  }, [coaching, isEdit, reset]);

  useEffect(() => {
    if (dateDebut && dureeMois && parseInt(dureeMois) > 0) {
      const d = new Date(dateDebut);
      d.setMonth(d.getMonth() + parseInt(dureeMois));
      setDateFin(d.toLocaleDateString('fr-FR'));
    } else {
      setDateFin('');
    }
  }, [dateDebut, dureeMois]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      prix:         parseInt(data.prix) || 0,
      duree_mois:   parseInt(data.duree_mois) || 1,
      montant_paye: parseInt(data.montant_paye) || 0,
      client:       parseInt(data.client),
    };
    if (!payload.observation) payload.observation = '';
    try {
      let coachingResult;
      if (isEdit) {
        coachingResult = await dispatch(updateCoaching({ id, payload })).unwrap();
      } else {
        coachingResult = await dispatch(createCoaching(payload)).unwrap();
      }
      const clientIdNum = parseInt(data.client);
      const uploads = Object.entries(documents).filter(([, f]) => f);
      let echecUpload = false;
      for (const [typeDoc, fichier] of uploads) {
        const form = new FormData();
        form.append('client',        clientIdNum);
        form.append('type_document', typeDoc);
        form.append('fichier',       fichier);
        try {
          await api.post('/clients/documents/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch {
          echecUpload = true;
        }
      }
      if (echecUpload) toast.warning("Coaching créé mais certains documents n'ont pas pu être envoyés.");
      toast.success(isEdit ? 'Coaching modifié avec succès.' : 'Coaching enregistré avec succès.');
      navigate('/coachings');
    } catch (err) {
      if (err?.coaching_existant_id) {
        const coachingId = Array.isArray(err.coaching_existant_id) ? err.coaching_existant_id[0] : err.coaching_existant_id;
        const rawMsg = err.message || err.detail;
        const msg = Array.isArray(rawMsg) ? rawMsg[0] : (rawMsg || 'Ce client a déjà un coaching en cours.');
        toast.warning(msg, { autoClose: false });
        if (window.confirm(`${msg}\n\nVoulez-vous accéder à ce coaching pour le renouveler ?`)) {
          navigate(`/coachings/${coachingId}?renouveler=1`);
        }
        return;
      }
      if (err?.__html__)        toast.error(err.__html__);
      else if (err?.__reseau__) toast.error(err.__reseau__);
      else if (err && typeof err === 'object') {
        const msgs = Object.values(err).flat().join(' | ');
        toast.error(msgs || "Erreur lors de l'enregistrement.");
      } else {
        toast.error("Erreur lors de l'enregistrement.");
      }
    }
  };

  const toggleDoc = (typeVal, fichier) => {
    setDocuments(prev => {
      const next = { ...prev };
      if (fichier) { next[typeVal] = fichier; }
      else { delete next[typeVal]; if (fileRefs.current[typeVal]) fileRefs.current[typeVal].value = ''; }
      return next;
    });
  };

  if (isEdit && loadingOne) {
    return (
      <div className="spinner" style={{ marginTop: 60 }}>
        <div className="spinner-ring" /> Chargement du coaching...
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Barre titre ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexShrink: 0 }}>
        <button className="header-btn btn-secondary btn-sm"
          onClick={() => navigate('/coachings')}
          style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <ArrowLeft size={13} /> Retour
        </button>
        <div>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', margin: 0 }}>
            {isEdit ? 'Modifier le coaching' : 'Nouveau coaching'}
          </h1>
          {isEdit && coaching && (
            <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
              {coaching.client_nom} — {coaching.domaine}
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ══ Grille 2 colonnes ══ */}
        <div style={{ flex: 1, minHeight: 0, display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: 12, overflow: 'hidden' }}>

          {/* ── COL 1 : Contrat ── */}
          <div className="card" style={{ ...S.card, overflow: 'hidden' }}>
            <SHead icon={Briefcase} title="Contrat" />
            <div style={{ ...S.grid2, alignContent: 'start', flex: 1 }}>

              <div style={{ ...S.fg, gridColumn: '1 / -1' }}>
                <label style={S.label}>Client <span style={{ color: '#C00000' }}>*</span></label>
                <select className={`form-control ${errors.client ? 'error' : ''}`} style={S.inp}
                  {...register('client', { required: 'Obligatoire' })}>
                  <option value="">— Sélectionner un client actif —</option>
                  {(clients || []).map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nom_cli} {c.prenom_cli}{c.telephone_principal ? ` — ${c.telephone_principal}` : ''}
                    </option>
                  ))}
                </select>
                {errors.client && <p style={S.err}>{errors.client.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>Type de coaching <span style={{ color: '#C00000' }}>*</span></label>
                <select className={`form-control ${errors.type_coaching ? 'error' : ''}`} style={S.inp}
                  {...register('type_coaching', { required: 'Obligatoire' })}>
                  {TYPE_COACHING.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {errors.type_coaching && <p style={S.err}>{errors.type_coaching.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>Produits <span style={{ color: '#C00000' }}>*</span></label>
                <select className={`form-control ${errors.domaine ? 'error' : ''}`} style={S.inp}
                  {...register('domaine', { required: 'Obligatoire' })}>
                  <option value="">— Sélectionner —</option>
                  {DOMAINES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                {errors.domaine && <p style={S.err}>{errors.domaine.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>SGI associée</label>
                <select className="form-control" style={S.inp} {...register('sgi')}>
                  {SGI_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div style={S.fg}>
                <label style={S.label}>Statut contrat</label>
                <select className="form-control" style={S.inp} {...register('statut')}>
                  <option value="en_cours">En cours</option>
                  <option value="expire">Expiré</option>
                  <option value="resilie">Résilié</option>
                </select>
              </div>

              <div style={S.fg}>
                <label style={S.label}>
                  <DollarSign size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                  Prix (FCFA) <span style={{ color: '#C00000' }}>*</span>
                </label>
                <input type="number" min="0" step="1"
                  className={`form-control ${errors.prix ? 'error' : ''}`} style={S.inp}
                  placeholder="Ex : 150 000"
                  {...register('prix', { required: 'Obligatoire', min: { value: 1, message: 'Prix > 0' } })} />
                {errors.prix && <p style={S.err}>{errors.prix.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>Montant déjà payé (FCFA)</label>
                <input type="number" min="0" step="1"
                  className="form-control" style={S.inp} placeholder="0"
                  {...register('montant_paye', { min: { value: 0, message: 'Montant ≥ 0' } })} />
                {errors.montant_paye && <p style={S.err}>{errors.montant_paye.message}</p>}
              </div>

              <div style={{ ...S.fg, gridColumn: '1 / -1' }}>
                <label style={S.label}>
                  <CreditCard size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                  Statut paiement <span style={{ color: '#C00000' }}>*</span>
                </label>
                <select className={`form-control ${errors.statut_paiement ? 'error' : ''}`} style={S.inp}
                  {...register('statut_paiement', { required: 'Obligatoire' })}>
                  {PAIEMENTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {errors.statut_paiement && <p style={S.err}>{errors.statut_paiement.message}</p>}
              </div>

            </div>
          </div>

          {/* ── COL 2 : 3 cartes empilées ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, overflow: 'hidden' }}>

            {/* Ligne 1 : Durée */}
            <div className="card" style={{ ...S.card, flexShrink: 0 }}>
              <SHead icon={Calendar} title="Durée du contrat" />
              <div style={S.grid2}>
                <div style={S.fg}>
                  <label style={S.label}>Date de début <span style={{ color: '#C00000' }}>*</span></label>
                  <input type="date" className={`form-control ${errors.date_debut ? 'error' : ''}`} style={S.inp}
                    {...register('date_debut', { required: 'Obligatoire' })} />
                  {errors.date_debut && <p style={S.err}>{errors.date_debut.message}</p>}
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Durée (mois) <span style={{ color: '#C00000' }}>*</span></label>
                  <input type="number" min="1" step="1"
                    className={`form-control ${errors.duree_mois ? 'error' : ''}`} style={S.inp}
                    placeholder="Ex : 3"
                    {...register('duree_mois', { required: 'Obligatoire', min: { value: 1, message: 'Min 1 mois' } })} />
                  {errors.duree_mois && <p style={S.err}>{errors.duree_mois.message}</p>}
                </div>
                {dateFin && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: '#EBF3FB', border: '1px solid #90CAF9',
                      borderRadius: 6, padding: '6px 12px' }}>
                      <Calendar size={12} color="#1F4E79" />
                      <span style={{ fontSize: 11, color: '#1F4E79' }}>Date de fin :</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#1F4E79' }}>{dateFin}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ligne 2 : Documents (création uniquement) */}
            {!isEdit && (
              <div className="card" style={{ ...S.card, flex: 1, minHeight: 0 }}>
                <SHead icon={Upload} title="Documents du client" />
                <p style={{ fontSize: 10, color: '#6B7280', margin: '0 0 8px', flexShrink: 0 }}>
                  Cochez et sélectionnez le fichier (PDF, JPG, PNG, DOC) — facultatif.
                </p>
                <div style={{ flex: 1, overflowY: 'auto', display: 'grid',
                  gridTemplateColumns: '1fr 1fr', gap: 6, alignContent: 'start' }}>
                  {TYPE_DOC_OPTIONS.map(opt => {
                    const fichier  = documents[opt.value] || null;
                    const inputId  = `doc-${opt.value}`;
                    const fileId   = `file-${opt.value}`;
                    const isActive = opt.value in documents;
                    return (
                      <div key={opt.value} style={{
                        border: `1px solid ${isActive ? '#90CAF9' : '#E5E7EB'}`,
                        borderRadius: 6, background: isActive ? '#F0F7FF' : '#FAFAFA',
                        padding: '6px 8px', transition: 'all 0.15s',
                      }}>
                        <label htmlFor={inputId}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input id={inputId} type="checkbox" checked={isActive}
                            onChange={e => {
                              if (e.target.checked) setDocuments(prev => ({ ...prev, [opt.value]: null }));
                              else toggleDoc(opt.value, null);
                            }}
                            style={{ width: 13, height: 13, cursor: 'pointer', accentColor: '#1F4E79' }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#1F2937' }}>{opt.label}</span>
                        </label>
                        {isActive && (
                          <div style={{ marginTop: 4 }}>
                            <input ref={el => { fileRefs.current[opt.value] = el; }}
                              id={fileId} type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
                              onChange={e => toggleDoc(opt.value, e.target.files[0] || null)} />
                            {fichier ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Paperclip size={11} color="#1E7145" />
                                <span style={{ fontSize: 10, color: '#1F2937', flex: 1, overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fichier.name}</span>
                                <button type="button" onClick={() => toggleDoc(opt.value, null)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 1 }}>
                                  <X size={11} />
                                </button>
                              </div>
                            ) : (
                              <label htmlFor={fileId} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 8px', borderRadius: 4,
                                border: '1px dashed #93C5FD', background: '#fff',
                                fontSize: 10, color: '#1F4E79', cursor: 'pointer', fontWeight: 600,
                              }}>
                                <Upload size={10} /> Choisir
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {Object.values(documents).filter(Boolean).length > 0 && (
                  <div style={{ marginTop: 6, padding: '4px 8px', background: '#E8F5E9',
                    borderRadius: 4, fontSize: 10, color: '#1E7145', fontWeight: 600, flexShrink: 0 }}>
                    {Object.values(documents).filter(Boolean).length} document(s) prêt(s)
                  </div>
                )}
              </div>
            )}

            {/* Ligne 3 : Observations */}
            <div className="card" style={{ ...S.card, flex: isEdit ? 1 : 0, flexShrink: isEdit ? 0 : 0,
              ...(isEdit ? {} : { minHeight: 90 }) }}>
              <SHead icon={FileText} title="Observations" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <label style={S.label}>Remarques / Notes internes</label>
                <textarea className="form-control"
                  style={{ flex: 1, resize: 'none', fontSize: 12, padding: '6px 10px',
                    minHeight: isEdit ? 0 : 60 }}
                  placeholder="Objectifs, conditions particulières…"
                  {...register('observation')} />
              </div>
            </div>

          </div>
        </div>

        {/* ── Boutons ── */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end',
          marginTop: 10, flexShrink: 0 }}>
          <button type="button" className="header-btn btn-secondary"
            onClick={() => navigate('/coachings')}
            style={{ padding: '8px 20px', fontSize: 12 }}>
            Annuler
          </button>
          <button type="submit" className="header-btn btn-primary" disabled={isSubmitting}
            style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 6 }}>
            <Save size={13} />
            {isSubmitting
              ? (isEdit ? 'Enregistrement...' : 'Création en cours...')
              : (isEdit ? 'Enregistrer les modifications' : 'Créer le coaching')}
          </button>
        </div>

      </form>
    </div>
  );
}
